import Box from '@app/components/common/Box';
import Button from '@app/components/common/Button';
import GreenDot from '@app/components/common/GreenDot';
import RedDot from '@app/components/common/RedDot';
import Table from '@app/components/common/Table';
import Typography from '@app/components/common/Typography';
import { dateTimeToSettingsTimeZone } from '@app/helpers/time/time';
import { displayFlexRowStyles } from '@app/theme/commonStyles';
import { createColumnHelper, Row, SortDirection } from '@tanstack/react-table';
import {
  EmployeeStatus,
  formatMoney,
  TEmployeesMainScreenRow,
} from '@westondev/tableturn-core';
import capitalize from 'lodash/capitalize';
import { useCallback, useContext, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { EmployeesScreenContext } from '../EmployeesContext';
import LoadingMore from './LoadingMore';
import {
  EMPLOYEE_STATUS_FILTERS_TEXT,
  TEmployeesMainScreenTableRow,
} from './types';

const columnHelper = createColumnHelper<TEmployeesMainScreenTableRow>();

const EmployeesTable = ({ t }: WithTranslation) => {
  //Redux

  const {
    setUser,
    users,
    fetchEmployees,
    hasFetched,
    isFetching,
    setDetailsFilters,
    filters,
    setSorting,
    sorting,
  } = useContext(EmployeesScreenContext);

  const usersFormatted = useMemo(
    () =>
      users.map(
        (user): TEmployeesMainScreenTableRow => ({
          ...user,
          id: user.orgUserId,
          role:
            user.roles.length === 0
              ? '--'
              : capitalize(
                  user.roles.length > 1
                    ? t('commonTexts.several')
                    : user.roles[0],
                ),
          actions: ' ',
        }),
      ),
    [users, t],
  );

  const handleOnSort = (sort: {
    sortDirection: SortDirection | false;
    columnId: string;
  }) => {
    if (sort.sortDirection === false) {
      setSorting({
        field: null,
        direction: null,
      });
      return;
    }
    const orderDirection = sort.sortDirection ? sort.sortDirection : null;

    setSorting({
      field: sort.columnId as keyof TEmployeesMainScreenRow,
      direction: orderDirection,
    });
  };

  const onRowClick = useCallback(
    (row: Row<TEmployeesMainScreenRow>) => {
      setDetailsFilters(filters);
      setUser(row.original);
    },
    [filters, setDetailsFilters, setUser],
  );

  const COLUMN_VALUES = useMemo(
    () => [
      columnHelper.accessor('pin', {
        header: t('managementModule.employeesScreen.employeesTable.pin'),
        cell: info => <Typography>{info.getValue()}</Typography>,
        size: 120,
        enableSorting: false,
      }),
      columnHelper.accessor('name', {
        header: t('managementModule.employeesScreen.employeesTable.name'),
        cell: info => (
          <Box
            csx={{ cursor: 'pointer' }}
            onClick={() =>
              onRowClick(info.row as unknown as Row<TEmployeesMainScreenRow>)
            }>
            <Typography color="semanticBlue" fontWeight="medium">
              {info.getValue() as string}
            </Typography>
          </Box>
        ),
        minSize: 200,
      }),
      columnHelper.accessor('currentStatus', {
        header: t('managementModule.employeesScreen.employeesTable.status'),
        cell: info => (
          <Box>
            <Box csx={[displayFlexRowStyles, { gap: 5 }]}>
              {info.getValue() === EmployeeStatus.ClockIn ? (
                <GreenDot size={15} />
              ) : (
                <RedDot size={15} />
              )}
              <Typography fontWeight="medium">
                {t(EMPLOYEE_STATUS_FILTERS_TEXT[info.getValue()])}
              </Typography>
            </Box>
            {info.row.original.currentStatusTime && (
              <Typography variant="caption" csx={{ fontStyle: 'italic' }}>
                {dateTimeToSettingsTimeZone(
                  info.row.original.currentStatusTime,
                ).toFormat('MM/dd hh:mm a')}
              </Typography>
            )}
          </Box>
        ),
        minSize: 150,
      }),
      columnHelper.accessor('role', {
        header: t('managementModule.employeesScreen.employeesTable.role'),
        cell: info => <Typography>{info.getValue()}</Typography>,
        invertSorting: true,
        minSize: 100,
      }),
      columnHelper.accessor('hours', {
        header: t('managementModule.employeesScreen.employeesTable.hours'),
        cell: info => <Typography>{info.getValue().toFixed(2)}</Typography>,
        minSize: 100,
      }),
      columnHelper.accessor('sales', {
        header: t('managementModule.employeesScreen.employeesTable.sales'),
        cell: info => (
          <Typography>{formatMoney(null, info.getValue())}</Typography>
        ),
        minSize: 160,
      }),
      columnHelper.accessor('tipsEarned', {
        header: t('managementModule.employeesScreen.employeesTable.tipsEarned'),
        cell: info => (
          <Typography>{formatMoney(null, info.getValue())}</Typography>
        ),
        minSize: 160,
      }),
      columnHelper.accessor('tipsPaid', {
        header: t('managementModule.employeesScreen.employeesTable.tipsPaid'),
        cell: info => (
          <Typography>{formatMoney(null, info.getValue())}</Typography>
        ),
        minSize: 160,
      }),
      columnHelper.accessor('tipsDue', {
        header: t('managementModule.employeesScreen.employeesTable.tipsDue'),
        cell: info => (
          <Box>
            <Typography>{formatMoney(null, info.getValue())}</Typography>
          </Box>
        ),
        minSize: 160,
      }),
      columnHelper.accessor('actions', {
        header: t('managementModule.employeesScreen.employeesTable.actions'),
        cell: () => (
          <Button
            csx={{
              width: '70%',
              height: 40,
            }}
            color="primary"
            onClick={() => null}>
            {t('managementModule.employeesScreen.employeesTable.payTip')}
          </Button>
        ),
        enableSorting: false,
        size: 180,
      }),
    ],
    [t, onRowClick],
  );

  return (
    <Box
      csx={{
        height: '100%',
        position: 'relative',
      }}>
      {hasFetched && (
        <Table
          cellCsx={{ height: '60px' }}
          scrollEnabled
          nestedScrollEnabled
          alignHeaders={{
            hours: 'right',
            sales: 'right',
            tipsEarned: 'right',
            tipsPaid: 'right',
            tipsDue: 'right',
          }}
          align={{
            hours: 'right',
            sales: 'right',
            tipsEarned: 'right',
            tipsPaid: 'right',
            tipsDue: 'right',
          }}
          columns={COLUMN_VALUES}
          data={users ? usersFormatted : []}
          persistentScrollbar
          listEmptyText={
            <Typography align="center">{t('commonTexts.noData')}</Typography>
          }
          manualSorting
          enableAlphabeticalSorting
          onEndReached={() => {
            fetchEmployees(true);
          }}
          onSortPress={handleOnSort}
          alwaysShowSortIcon
          columnSorting={
            sorting.field
              ? [{ id: sorting.field, desc: sorting.direction === 'desc' }]
              : []
          }
        />
      )}
      {isFetching && hasFetched && (
        <LoadingMore>
          <Typography>
            {t('managementModule.employeesScreen.employeesTable.loading')}
          </Typography>
        </LoadingMore>
      )}
    </Box>
  );
};

export default EmployeesTable;
